import { Mic } from "assets/svg";
import classNames from "classnames";
import { AudioPlayer } from "components/AudioPlayer/AudioPlayer";
import { Button } from "components/Button/Button";
import { AudioTroubleshootModal } from "components/modals/AudioTroubleshootModal/AudioTroubleshootModal";
import { API_SAVE_FAILURE_EVENT, ORGANIZATIONS, QUESTION_TYPE, SPONTANEOUS_RECORDING_TIME } from "constant";
import { useCallback, useEffect, useRef, useState } from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import { storage } from "services/storage";
import analytics from "services/analytics";
import stopAudio from "../../assets/icons/stop.svg"
import AudioRipple from "./AudioRipple";
import { useTranslation } from "react-i18next";

// Minimum mic-level average (0-255 scale) to count a frame as containing sound.
const AUDIO_VOLUME_THRESHOLD = 10;
/* Minimum fraction of the recording that must contain sound.
   Deliberately low: the analyser averages across all 512 FFT bins, but speech
   occupies only ~12% of them, so even a clear voice yields a frame average
   barely above the threshold. Add the natural pauses between words and a
   perfectly good answer lands near 0.5 — which is why a 0.5 floor rejected
   real recordings. The variance check below is what actually screens out a
   steady buzz, so this only has to catch true silence. */
const MIN_LOUD_FRAME_RATIO = 0.08;
/* A steady buzz, hum or fan clears the loud-frame ratio on every frame, so
   loudness alone cannot tell it apart from speech. Speech rises and falls
   between syllables, so we also require the level to vary: the gap between the
   quiet and loud ends of the recording must exceed this (0-255 scale).

   Kept low for the same reason as the ratio above — these are averages across
   all FFT bins, so a soft speaker's swing is far smaller than it sounds. A
   steady tone still measures near zero range, so this separates the two
   cleanly without rejecting quiet voices. */
const MIN_LEVEL_RANGE = 8;
// Frames needed before the variation test is meaningful (~1s of audio).
const MIN_FRAMES_FOR_VARIANCE = 20;

export const AudioRecorder = ({
  questionType = null,
  onDataAvailable = () => { },
  loading = false,
  hideTimer = false,
  showMicVolumeLevel = true,
  audioDeviceId = storage.get.audioDevice(),
  getBlobData = () => { },
  onStatusChange = () => { },
  onSubmit = () => { },
  timeToSolve = SPONTANEOUS_RECORDING_TIME,
  isOffline = false,
  gameTime = null,
  previewBeforeSubmit = false,
}) => {

  const [isRecording, setIsRecording] = useState(false);
  const [recorder, setRecorder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Saving")
  const [recordTimer, setRecordTimer] = useState(SPONTANEOUS_RECORDING_TIME);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [audioContext, setAudioContext] = useState(null);
  const [errorMessage, setErrorMessage] = useState(false);
  const [showTroubleshoot, setShowTroubleshoot] = useState(false);
  const [isTimerReady, setIsTimerReady] = useState(false)
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null)
  const [previewType, setPreviewType] = useState("audio/mp4")
  const [recordedDuration, setRecordedDuration] = useState(0)

  const buttonRef = useRef(null);
  const recordedAudioFile = useRef(null);
  const workerRef = useRef(null)
  const audioFrameCountRef = useRef(0);
  const loudFrameCountRef = useRef(0);
  // Loudest and quietest frames this recording, used to measure level variation.
  const loudestFrameRef = useRef(0);
  const quietestFrameRef = useRef(Infinity);
  const submitAfterStopRef = useRef(false)
  const recordTimerRef = useRef(recordTimer)

  const { t } = useTranslation()

  const organization = storage.get.org();
  const spotlightOrgFlag = organization === ORGANIZATIONS.SPOTLIGHT

  const stopRecorderStream = useCallback((activeRecorder = recorder) => {
    activeRecorder?.stream?.getTracks().forEach((track) => track.stop());
  }, [recorder]);

  const requestRecorder = useCallback(async () => {
    const deviceId = audioDeviceId;
    const constraints = { audio: deviceId ? { deviceId } : true };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);

    // logic for showing microphone input level
    if (showMicVolumeLevel) {
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      const scriptProcessor = audioContext.createScriptProcessor(2048, 1, 1);

      analyser.smoothingTimeConstant = 0.8;
      analyser.fftSize = 1024;

      microphone.connect(analyser);
      analyser.connect(scriptProcessor);
      scriptProcessor.connect(audioContext.destination);
      scriptProcessor.onaudioprocess = function () {
        const array = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(array);
        const arraySum = array.reduce((a, value) => a + value, 0);
        const average = arraySum / array.length;
        setVolumeLevel(average);

        audioFrameCountRef.current += 1;
        if (average > AUDIO_VOLUME_THRESHOLD) {
          loudFrameCountRef.current += 1;
        }

        // Track the spread between quiet and loud frames. A steady tone keeps
        // both ends close together; speech pushes them apart.
        if (average > loudestFrameRef.current) loudestFrameRef.current = average;
        if (average < quietestFrameRef.current) quietestFrameRef.current = average;
      };

      // Saving context to close it once the recording is stopped
      setAudioContext(audioContext);
    }

    return new MediaRecorder(stream);
  }, [audioDeviceId, showMicVolumeLevel]);

  const initializeRecorder = useCallback(async () => {
    try {
      const recorderInstance = await requestRecorder();
      setRecorder(recorderInstance);
    } catch (error) {
      analytics.track('audio_record_failed', {
        reason: 'getusermedia_error',
        question_type: questionType,
        error: error?.name || String(error),
      });
      console.error(error);
    }
  }, [requestRecorder, questionType]);

  // this method gets fired when recorder.stop method is invoked
  const handleData = useCallback(
    async (e) => {
      if (!isOffline) {
        audioContext?.close();
        // Safari can keep the mic reserved after the initial test unless the
        // completed recorder's stream is explicitly released.
        stopRecorderStream();
        // audio format
        const file = new File([e.data], "audio.wav", {
          type: "audio/wav",
          lastModified: Date.now(),
        });
        recordedAudioFile.current = file;

        const loudFrameRatio = audioFrameCountRef.current > 0
          ? loudFrameCountRef.current / audioFrameCountRef.current
          : 0;

        // Loudness alone passes a steady buzz, so also require the level to
        // vary. Skipped for very short clips, where there are too few frames
        // to judge variation and rejecting would be a false negative.
        const quietest = quietestFrameRef.current === Infinity ? 0 : quietestFrameRef.current;
        const hasEnoughFrames = audioFrameCountRef.current >= MIN_FRAMES_FOR_VARIANCE;
        const levelVaried = !hasEnoughFrames
          || loudestFrameRef.current - quietest >= MIN_LEVEL_RANGE;

        const hasAudibleContent = !showMicVolumeLevel
          || (loudFrameRatio >= MIN_LOUD_FRAME_RATIO && levelVaried);

        if (!hasAudibleContent) {
          analytics.track('audio_record_failed', {
            reason: 'no_audible_content',
            question_type: questionType,
            loud_frame_ratio: Number(loudFrameRatio.toFixed(3)),
          });
          setErrorMessage(
            t("audio.no_audio")
          );
          // Surface the fix-it steps automatically the first time audio fails.
          setShowTroubleshoot(true);
          setRecorder(null);
          setRecordTimer(timeToSolve || SPONTANEOUS_RECORDING_TIME);
          setAudioPreviewUrl(null);
          return;
        }

        analytics.track('audio_recorded', {
          question_type: questionType,
          preview_enabled: !!previewBeforeSubmit,
        });

        if (previewBeforeSubmit && !submitAfterStopRef.current) {
          const recordingLimit = timeToSolve || SPONTANEOUS_RECORDING_TIME;
          const elapsedRecordingTime = recordingLimit - Math.max(recordTimerRef.current, 0);
          // Preview must use the real container MIME (audio/mp4 on iOS, audio/webm
          // on Android). Forcing audio/wav makes Safari refuse to play the blob.
          const realType = e.data?.type || recorder?.mimeType || "audio/mp4";
          const previewBlob = new Blob([e.data], { type: realType });
          const audioUrl = URL.createObjectURL(previewBlob);
          setRecordedDuration(Math.max(elapsedRecordingTime, 0));
          setPreviewType(realType);
          setAudioPreviewUrl(audioUrl);
          setRecorder(null);
          getBlobData(e.data);
          return;
        }

        submitAfterStopRef.current = false;
        triggerLoadingMsgUpdate(t("audio.taking_longer"), 5)
        // sending file data for backend apis
        onDataAvailable(file);

        // sending blob data for audioTestingModal component
        getBlobData(e.data);
      }
    },
    [audioContext, getBlobData, showMicVolumeLevel, onDataAvailable, isOffline, previewBeforeSubmit, stopRecorderStream, t, timeToSolve]
  );

  const triggerLoadingMsgUpdate = (text = "", postSeconds = 0) => {
    const loadingMsgTimeout = setTimeout(() => {
      setLoadingMessage(text)
      clearTimeout(loadingMsgTimeout)
    }, postSeconds * 1000)
  }

  const startRecording = () => {
    setAudioPreviewUrl(null);
    setErrorMessage(false);
    setShowTroubleshoot(false);
    setRecordedDuration(0);
    recordedAudioFile.current = null;
    submitAfterStopRef.current = false;
    audioFrameCountRef.current = 0;
    loudFrameCountRef.current = 0;
    loudestFrameRef.current = 0;
    quietestFrameRef.current = Infinity;
    setIsRecording(true);
    onStatusChange("recording");
  };

  const stopRecording = () => {
    setIsRecording(false);
    onStatusChange("inactive");
  };

  // Proceed Anyway is disabled: users must not continue when no audio is detected. Kept for future use.
  // const onProceedAnyway = () => {
  //   triggerLoadingMsgUpdate(t("audio.taking_longer"), 5)
  //   if (onSubmit) onSubmit();
  //   if (onDataAvailable) onDataAvailable(recordedAudioFile.current);
  // };

  const handleSaveFailure = () => {
    setIsLoading(false)
  }

  const submitRecordedAudio = () => {
    if (!recordedAudioFile.current) return;

    triggerLoadingMsgUpdate(t("audio.taking_longer"), 5)
    onDataAvailable(recordedAudioFile.current);
  }

  const reRecord = () => {
    analytics.track('audio_re_recorded', {
      question_type: questionType,
    });
    setAudioPreviewUrl(null);
    setRecorder(null);
    setErrorMessage(false);
    setShowTroubleshoot(false);
    setRecordedDuration(0);
    recordedAudioFile.current = null;
    setRecordTimer(timeToSolve || SPONTANEOUS_RECORDING_TIME);
    onStatusChange("inactive");
  }

  useEffect(() => {
    if (timeToSolve)
      setRecordTimer(timeToSolve)
  }, [timeToSolve]);

  useEffect(() => {
    return () => {
      if (recorder?.state === "recording") recorder.stop();
      stopRecorderStream(recorder);
    };
  }, [recorder, stopRecorderStream]);

  useEffect(() => {
    recordTimerRef.current = recordTimer;
  }, [recordTimer])

  /**
   * Initialize timer on recording and get recording 59 seconds
   * After which make API call by clicking button throgh ref
   * Since API for speech checking restricted to file < 60 seconds.
   */
  useEffect(() => {
    if (hideTimer) return;

    let intervalId;
    if (isRecording) {
      // removed setInterval from here. Using web worker instead.
    }
    // In addition to recordTimer, if gameTime (quizTime) hits 0, stop recprdng and save answer.
    if (
      ((recordTimer <= 0) || (gameTime !== null && gameTime === 0))
      && buttonRef.current) {
      submitAfterStopRef.current = true;
      buttonRef.current.click();
    }
    return () => clearInterval(intervalId);
  }, [isRecording, recordTimer, hideTimer, gameTime]);

  // Refresh timer if loading changes state -> Only happens when error in audio upload
  useEffect(() => {
    setIsLoading(prevState => {
      if (prevState) {
        setRecordTimer(timeToSolve || SPONTANEOUS_RECORDING_TIME);
        setAudioPreviewUrl(null);
        setRecordedDuration(0);
      }
      return loading;
    });
  }, [loading]);

  useEffect(() => {
    if (audioPreviewUrl) return;

    if (recorder === null) {
      if (isRecording) {
        initializeRecorder();
      }
      return;
    }

    // Manage recorder state.
    if (isRecording && recorder.state !== "recording") {
      recorder.start();

      // resuming audio context which give microphone volume info
      if (audioContext) audioContext.resume();
    } else if (!isRecording) {
      // execute stop only when state is inactive -> Android Fix
      recorder.state !== "inactive" && recorder.stop();

      // Suspends the progression of time in the audio context, temporarily halting audio hardware access and reducing CPU/battery usage in the process.
      if (audioContext) audioContext.suspend();
    }

    recorder.addEventListener("dataavailable", handleData);
    return () => {
      recorder.removeEventListener("dataavailable", handleData);
      // if (recorder.state === "recording") recorder.stop();
    };
  }, [recorder, isRecording, audioContext, handleData, initializeRecorder, audioPreviewUrl]);

  useEffect(() => {
    if (isOffline) {
      setIsRecording(false)
      setRecordTimer(timeToSolve || SPONTANEOUS_RECORDING_TIME)
      setIsLoading(false)
      setAudioPreviewUrl(null)
      setRecordedDuration(0)
    }
  }, [isOffline, timeToSolve])

  useEffect(() => {
    return () => {
      if (audioPreviewUrl) URL.revokeObjectURL(audioPreviewUrl);
    }
  }, [audioPreviewUrl])

  /**
   * Start the timer once user presses speak btn.
   */
  useEffect(() => {
    if (isRecording) {
      workerRef.current.postMessage("startTimer")
    } else {
      if (workerRef.current) {
        workerRef.current.postMessage("stopTimer")
      }
    }
  }, [isRecording])

  /**
   * Load the timer worker on component mount.
   * By default isTimerReady is false. It is set to true only after worker is loaded with "timerLoaded" event.
   * "tick" event is used to progress the recording timer.
   * Terminate worker on component unmount.
   */
  useEffect(() => {
    workerRef.current = new Worker(new URL("../../utils/timerWorker.js", import.meta.url))
    workerRef.current.onmessage = (event) => {
      if (event.data === "timerLoaded") {
        setIsTimerReady(true)
      } else if (event.data === "tick") {
        setRecordTimer((prevTime) => prevTime - 1)
      }
    }

    window.addEventListener(API_SAVE_FAILURE_EVENT, handleSaveFailure)
    return () => {
      workerRef.current.terminate()
      return () => window.removeEventListener(API_SAVE_FAILURE_EVENT, handleSaveFailure)
    }
  }, [])

  // Microphone volume blocks
  const audioBlocks = Array.from({ length: 6 }, (_, index) => {
    const numberOfBlocksToColor = Math.round(volumeLevel / 6);

    return (
      <div
        key={index}
        className="h-4 w-1 flex-1 rounded-[4px]"
        style={{
          backgroundColor:
            index < numberOfBlocksToColor ? "#FFF" : "rgba(255,255,255,0.3)",
        }}
      />
    );
  });

  const recordingLimit = timeToSolve || SPONTANEOUS_RECORDING_TIME;
  const shouldShowShortRecordingWarning = audioPreviewUrl && recordedDuration < (recordingLimit / 2);

  return (
    <div
      className={classNames(`mt-10 flex min-h-[220px] w-full min-w-0 flex-col items-center gap-4`, {
        "relative mt-12": spotlightOrgFlag,
      })}
    >
      <AudioTroubleshootModal
        visible={showTroubleshoot}
        onClose={() => setShowTroubleshoot(false)}
      />
      {audioPreviewUrl ? (
        <div className={classNames("flex w-full max-w-full min-w-0 flex-col items-center gap-6 lg:max-w-[644px]", {
          "relative z-[3] px-3": spotlightOrgFlag
        })}>
          <div className="w-full">
            <AudioPlayer src={audioPreviewUrl} type={previewType} />
          </div>
          {shouldShowShortRecordingWarning && (
            <p className={classNames("mx-auto max-w-[520px] text-center text-xs leading-5 text-cocoa-brown", {
              "text-[#fff] opacity-80": spotlightOrgFlag
            })}>
              <FaExclamationTriangle className="mr-2 inline-block align-[-2px] text-[#F4C430]" size={16} />
              {t("audio.short_recording_warning")}
            </p>
          )}
          <div className="flex w-full flex-col items-center justify-center gap-3">
            <Button
              className={classNames("inline-flex w-full min-w-0 items-center justify-center !border-[#2E1500] !bg-[#2E1500] !px-6 !text-center !text-white sm:w-auto sm:!min-w-[250px] sm:!px-10", {
                "!border-[#2E1500] !bg-[#2E1500] !text-white": spotlightOrgFlag
              })}
              onClick={submitRecordedAudio}
              disabled={isLoading}
            >
              {!isLoading ? t("submit") : `${t("submitting")}...`}
            </Button>
            <Button
              className={classNames("inline-flex w-auto !min-w-[128px] items-center justify-center !border-[#D3B7A0] !bg-transparent !px-4 !py-1.5 !text-center !text-sm !font-normal !text-[#2E1500] sm:!text-sm", {
                "!border-[#D3B7A0] !text-[#2E1500]": spotlightOrgFlag
              })}
              onClick={reRecord}
              disabled={isLoading}
            >
              {t("audio.re_record")}
            </Button>
          </div>
        </div>
      ) : isRecording ? (
        <>
          <button
            className={classNames("flex items-center transition-all hover:shadow-md bg-bistre",
              {
                "bg-[#fff] rounded-full p-[30px] relative z-[1]": spotlightOrgFlag,
                "rounded-3xl py-2 px-4 border border-[#505050ef]": !spotlightOrgFlag
              }
            )}
            ref={buttonRef}
            onClick={stopRecording}
            disabled={isLoading}
          >
            {
              spotlightOrgFlag
                ? <Mic />
                : <></>
            }
            {showMicVolumeLevel && !spotlightOrgFlag && (
              <div className="flex items-center gap-1">{audioBlocks}</div>
            )
            }
            {
              !spotlightOrgFlag
                ? <>
                  <p className="text-[#FFF] ml-3 uppercase">{previewBeforeSubmit ? t("audio.done") : t("submit")}</p>
                  <img src={stopAudio} alt="stop" className="ml-3" />
                </>
                : <></>
            }
          </button>
          {
            spotlightOrgFlag
              ? <AudioRipple volumeLevel={volumeLevel} />
              : <></>
          }
          {
            spotlightOrgFlag
              ? <p className="text-[12px] text-[#fff] font-light opacity-60 uppercase">{t("audio.tap_to_stop")}</p>
              : <></>
          }
          {!hideTimer && (
            <p className=" font text-sm">
              {t("audio.time_left")}:
              <span className="font-medium"> {recordTimer}</span>
            </p>
          )}

        </>
      ) : (
        <>
          <button
            className={classNames(
              "flex items-center transition-all hover:shadow-md border border-[#505050ef]",
              {
                "bg-gray-300 cursor-not-allowed": isLoading,
                "opacity-50 cursor-wait": !isTimerReady // Disabled styling while timer worker is getting loaded.
              },
              {
                "bg-[#fff] rounded-full p-[30px] relative z-[1]": spotlightOrgFlag,
                "rounded-3xl py-2 px-4": !spotlightOrgFlag
              }
            )}
            onClick={startRecording}
            disabled={isLoading || !isTimerReady} // Keep btn disabled while it is submitting audio and while timer worker is getting loaded.
          >
            <Mic />
            {
              !spotlightOrgFlag
                ? <p className="text-[#2E1500] font-medium ml-3 uppercase">{t("audio.speak")}</p>
                : <></>
            }
          </button>
          {
            spotlightOrgFlag
              ? <AudioRipple volumeLevel={volumeLevel} />
              : <></>
          }
          {
            spotlightOrgFlag
              ? <p className="text-[12px] font-light text-[#fff] opacity-60 uppercase">{t("audio.tap_to_record")}</p>
              : <></>
          }
          {
            isLoading ?
              <div className="audio-submission flex items-end justify-start opacity-70">
                <p className={classNames("font-medium text-[16px] ml-2 relative bottom-[-5px] tracking-[0.3px]", {
                  "text-[#000]": !spotlightOrgFlag,
                  "text-[#fff] opacity-50": spotlightOrgFlag
                })}>
                  {loadingMessage}
                </p>
                <div className="typing__dot relative bottom-[-1px] scale-[0.6]" />
                <div className="typing__dot relative bottom-[-1px] scale-[0.6]" />
                <div className="typing__dot relative bottom-[-1px] scale-[0.6]" />
              </div> : <></>
          }
          {!!errorMessage && (
            <div className={classNames("flex flex-col items-center", {
              "hidden": isLoading
            })}>
              <p className="text-base text-center text-red-500 mb-4">{errorMessage}</p>
              <button
                type="button"
                className={classNames("text-sm underline mb-2", {
                  "text-[#fff] opacity-80": spotlightOrgFlag,
                  "text-cocoa-brown": !spotlightOrgFlag
                })}
                onClick={() => setShowTroubleshoot(true)}
              >
                {t("audio.troubleshoot.link")}
              </button>
              {/* Proceed Anyway is disabled: users must not continue when no audio is detected. Kept for future use. */}
              {/* <Button
                className={classNames({
                  "bg-[#fff]": spotlightOrgFlag
                })}
                onClick={onProceedAnyway}
              >
                {t("audio.proceed_anyway")}
              </Button> */}
            </div>
          )}
        </>
      )}
    </div>
  );
};
